import { Link } from '@tanstack/react-router';
import { UserRound, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';

import { reference } from '../../generated/reference';
import type { DocsCustomer } from '../../schemas/docs-storage-schema';

const login = reference.operations.loginCustomer;

/**
 * Who the `X-Customer-Token` belongs to. Any response with an access and refresh token signs a
 * customer in for this tab; customer routes without one point at the login endpoint.
 */
export function CustomerTokenChip({
  customer,
  required,
  onClear,
}: {
  customer: DocsCustomer | null;
  required: boolean;
  onClear: () => void;
}) {
  const { t } = useTranslation('docs');

  if (!customer) {
    if (!required) return null;
    return (
      <p className="text-muted-foreground rounded-md border border-dashed px-3 py-2 text-xs">
        {t('tryIt.customer.missing')}{' '}
        {login ? (
          <Link
            to="/docs/api/$tag/$operation"
            params={{ tag: login.tag, operation: login.slug }}
            className="text-foreground font-medium underline underline-offset-4"
          >
            {login.summary}
          </Link>
        ) : null}
      </p>
    );
  }

  return (
    <div className="bg-muted/60 flex items-center gap-2 rounded-md px-3 py-1.5 text-xs">
      <UserRound className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1 truncate">
        {customer.email
          ? t('tryIt.customer.signedIn', { email: customer.email })
          : t('tryIt.customer.signedInNoEmail')}
        {required ? (
          <span className="text-muted-foreground"> · {t('tryIt.customer.sendsHeader')}</span>
        ) : null}
      </span>
      <Button type="button" variant="ghost" size="sm" className="h-6 px-1.5" onClick={onClear}>
        <X aria-hidden />
        {t('tryIt.customer.clear')}
      </Button>
    </div>
  );
}
